import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  deleteQuiz as deleteQuizRequest,
  getOperatorsOptions,
  getQuizDetailsQueryKey,
  getQuizzesOptions,
  getQuizzesQueryKey,
  Quiz,
} from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';
import { usePlanOwnerOptions } from '@/hooks/usePlanOwner';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import {
  AddSortFields,
  addSortFields,
  useSortOptions,
} from '@/hooks/useSortOptions';

import { isApiError } from '@/utils/request';
import { withRoles } from '@/utils/withRoles';

import GenericError from '@/components/Error/GenericError';
import { Fab } from '@/components/Fab/Fab';
import Filter, { FilterDesktop, FilterMobile } from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import NoMessage from '@/components/NoMessage/NoMessage';
import AddQuizPopUp from '@/components/popups/AddQuizPopUp';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { routes } from '@/constants';

import { queryClient } from '@/services/api';

import QuizCard from './QuizCard/QuizCard';

import { sourceDataFamily, useDataView } from '@/atoms/data-view';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import { quizDraftAtom } from '@/atoms/quiz-draft';

const sourceData = sourceDataFamily('build-quizzes');

const DATAVIEW_KEY = 'manage-quizzes';

const BuildQuizzesTab = () => {
  const { t } = useTranslation();
  const { data: user } = useAuth();

  const userIsStaff = user?.isStaff;
  const isLicenseeUser = user?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const setQuizDraft = useSetAtom(quizDraftAtom);
  const navigate = useNavigate();
  const [showCreateQuizPopUp, setShowCreateQuizPopUp] = useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const sortOptions = useSortOptions({ manageQuizzes: true });

  const locationsWithManageQuizzes =
    user?.locationsWithRoles
      .filter(location => location.permissions.canManageQuizzes)
      .map(location => location.location.number) ?? [];
  const canManageQuizzesAnywhere = locationsWithManageQuizzes.length > 0;

  const tabsActive = usePlansShouldShowTabs();

  const invalidateQuizzes = (id?: string) => {
    queryClient.invalidateQueries({
      queryKey: getQuizzesQueryKey(),
      refetchType: 'all',
    });
    if (id) {
      // With refetchOnMount: false, invalidate alone leaves stale details cache
      // for the quiz editor. Remove it so the edit page fetches.
      queryClient.removeQueries({
        queryKey: getQuizDetailsQueryKey({
          path: {
            quizId: id,
          },
        }),
      });
    }
  };

  const deleteQuiz = async (id: string, callback?: () => void) => {
    try {
      await deleteQuizRequest({ path: { quizId: id } });
      invalidateQuizzes(id);
      callback?.();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  const {
    data: quizzes,
    isFetching,
    isPending,
    error: errorGettingQuizzes,
  } = useQuery({
    ...getQuizzesOptions(),
  });

  const isFetchingQuizzes = isFetching || isPending;

  useScrollMemory({
    container: 'body',
    key: DATAVIEW_KEY,
    enabled: !isFetchingQuizzes,
  });

  const { v2Quizzes } = useFlags();
  const { ownerOptions, resolvePlanOwner } = usePlanOwnerOptions({
    isEligible: location => location.permissions.canManageQuizzes,
  });

  const { data: operators, error: errorGettingOperators } = useQuery({
    ...getOperatorsOptions(),
    enabled: !v2Quizzes,
  });

  const operatorsWhereUserCanManageQuizzes = (operators ?? []).filter(
    (operator): operator is typeof operator & { id: string; name: string } =>
      !!operator.id &&
      !!operator.name &&
      !!operator.locations?.some(operatorLocation =>
        locationsWithManageQuizzes.includes(operatorLocation),
      ),
  );

  const createQuizOptions = v2Quizzes
    ? ownerOptions.map(owner => ({ id: owner.ownerId, label: owner.label }))
    : operatorsWhereUserCanManageQuizzes.map(operator => ({
        id: operator.id,
        label: operator.name,
      }));

  const setSource = useSetAtom(sourceDataFamily('manage-quizzes'));

  useEffect(() => {
    setSource((quizzes ?? []).map(addSortFields));
  }, [quizzes, setSource]);

  const [
    {
      display: { items, total },
      filters,
      sort,
    },
    { setSort, setSearch, resetSearch },
  ] = useDataView<AddSortFields<Quiz>>(DATAVIEW_KEY);

  const setSourceData = useSetAtom(sourceData);

  useEffect(() => {
    if (isLicenseeUser) {
      const licenseeLocationQuizzes = quizzes?.filter(
        quizToFilter =>
          quizToFilter.operatorId === activeLicenseeLocation?.number,
      );
      setSourceData(licenseeLocationQuizzes ?? []);
    } else {
      setSourceData(quizzes ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLicenseeLocation?.number, isLicenseeUser, quizzes]);

  const isValidQuizName = (quizName: string, ownerId?: string) => {
    const isMissingOwner = !ownerId && (v2Quizzes || !userIsStaff);
    if (!quizName || isMissingOwner) {
      setShowInvalidNameError(true);
      console.error('invalid quiz name or missing owner');
      datadogRum.addError(
        new Error(
          `Build quizzes error - no quiz name or no owner: ${JSON.stringify({
            quizName,
            ownerId,
          })}`,
        ),
      );
      return false;
    }
    return true;
  };

  const onAddQuiz: React.ComponentProps<
    typeof AddQuizPopUp
  >['submitHandler'] = ({ selectedId, quizNameInput }) => {
    setShowCreateQuizPopUp(false);
    const trimmedName = quizNameInput.trim();
    const ownerOrOperatorId = v2Quizzes
      ? resolvePlanOwner(selectedId).ownerId
      : selectedId;
    if (!isValidQuizName(trimmedName, ownerOrOperatorId)) {
      return;
    }
    // Clear any in-flight quiz draft, since we are intentionally starting
    // from scratch.
    setQuizDraft(null);
    navigate(
      {
        pathname: routes.plans.managedQuizzesNew,
      },
      {
        state: {
          ...(v2Quizzes
            ? { ownerId: ownerOrOperatorId }
            : { operator: { id: ownerOrOperatorId } }),
          quizName: trimmedName,
          buildStartedAt: Date.now(),
        },
      },
    );
  };

  const onDeleteQuiz = (quizId: string, quizName: string) => {
    deleteQuiz(quizId, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {quizName} {t('TrainingPlans.toastMessage.deleted')}
        </ToastMessageBlock>
      ));
    });
  };

  if (isApiError(errorGettingOperators ?? errorGettingQuizzes)) {
    const error = errorGettingOperators ?? errorGettingQuizzes;
    datadogRum.addError(error);
    return <GenericError />;
  }

  const search: FilterSearchBarProps = {
    onChange: e => {
      setSearch(e.target.value, 'nameLocalLang');
    },
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.filtering.searchQuizzes'),
    value: filters.search,
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    sortOnly: true,
    text: `${t('TrainingPlans.filtering.show')} ${
      total
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    after: <Filter.SearchBar {...search} />,
  };

  const filterOptionsMobile = {
    filterAndSortButtonOptions,
    search: <Filter.SearchBar {...search} />,
  };

  return (
    <>
      <LoadingOverlay isOpen={isFetchingQuizzes} fixed />
      <div className="relative flex max-w-full grow flex-col">
        <div className="flex w-full flex-col">
          {!isFetchingQuizzes && <FilterMobile {...filterOptionsMobile} />}
          {/* Top Row */}
          <div>
            <div className="flex justify-end md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={() => setSearch('')}
                options={sortOptions}
                showClear={false}
                text={`${total} ${t('TrainingPlans.filtering.plans')}`}
                title={t('TrainingPlans.filtering.manageQuizzes')}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
              />
            </div>
            <div className="mt-2.5 flex justify-between">
              {/* Left Side */}
              {!isFetchingQuizzes && (
                <FilterDesktop {...filterOptions} tabsActive={tabsActive} />
              )}

              {/* Right Side */}
              <div className="flex w-full min-w-0 flex-col gap-4">
                {!isFetchingQuizzes &&
                  items.map(quizDetails => (
                    <QuizCard
                      key={quizDetails.id}
                      onDeleteQuiz={onDeleteQuiz}
                      quizDetails={quizDetails}
                    />
                  ))}
                {quizzes?.length === 0 && !isFetchingQuizzes && (
                  <NoMessage
                    message={t('TrainingPlans.buildQuizzes.noQuizzesLoaded')}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {canManageQuizzesAnywhere &&
          (!!createQuizOptions.length || !!userIsStaff) && (
            <>
              <Fab
                isOpen={showCreateQuizPopUp}
                onClick={() => {
                  // Clear any in-flight quiz draft, since we are intentionally
                  // creating a new quiz.
                  setQuizDraft(null);
                  setShowCreateQuizPopUp(true);
                }}
                tooltip={t('TrainingPlans.buildQuizzes.createQuizHover')}
                icon="quiz-plus"
              />
              <AddQuizPopUp
                isLicenseeUser={!!isLicenseeUser}
                open={showCreateQuizPopUp}
                options={createQuizOptions}
                setOpen={setShowCreateQuizPopUp}
                submitHandler={onAddQuiz}
              />
            </>
          )}
      </div>
      <ConfirmationModal
        bodyText={t('InvalidQuizName.paragraphText')}
        headerText={t('InvalidQuizName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};

const BuildQuizzesTabWithRoles = withRoles(BuildQuizzesTab, 'quizzes.create');

export default BuildQuizzesTabWithRoles;
